import React, { useRef, useCallback, ReactNode } from 'react';
import { Row } from '@prisjakt/ui/list/variants';
import { layout } from '@prisjakt/ui-base';
import PriceLabel from '@prisjakt/ui-price-label';
import Rating from '@prisjakt/ui-rating';
import { CountTranslation } from '@prisjakt/ui-rating/src/types';
import Lazy from '../../../../../components/lazy';
import { CampaignIdsByProductId } from '../../../../../hooks/campaigns-by-product-id';
import { SortOptionEnum } from '../../../../product/components/sort-options/types';
import { Product } from '../../../../../types';
import ActiveCampaignBadge from '../../active-campaign-badge';
import HighlightedSearchResult from '../highlighted-search-result';
import { ContainerAdsList } from './ads-container';
import * as S from './styles';
import { ProductComparisonProps } from '../types';

const NO_CAMPAIGNS: never[] = [];
const RATING_TRANSLATION = {
  domain: 'product',
  key: 'product-card.n-ratings',
} as CountTranslation;

type MobileListViewItemProps = {
  product: Product;
  productCampaigns?: number[];
  highlightedName?: ReactNode;
} & ProductComparisonProps;

export const MobileListViewItem = ({
  product,
  isSelectingProducts,
  selectedProducts,
  selectProductById,
  deselectProductById,
  productCampaigns = NO_CAMPAIGNS,
  highlightedName,
}: MobileListViewItemProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const goToProduct = useCallback(() => {
    if (!ref.current) {
      return;
    }
    ref.current.click();
  }, [ref]);
  const isSelected = selectedProducts.some(p => p.id === product.id);
  const onSelect = useCallback(
    e => {
      e.preventDefault(); // Prevents two callbacks when clicking the checkbox directly
      selectProductById(product.id, 'productCard');
    },
    [product.id, selectProductById]
  );
  const onDeselect = useCallback(
    e => {
      e.preventDefault(); // Prevents two callbacks when clicking the checkbox directly
      deselectProductById(product.id, 'productCard');
    },
    [product.id, deselectProductById]
  );
  const onToggleSelected = isSelected ? onDeselect : onSelect;
  const onClickRow = isSelectingProducts ? onToggleSelected : goToProduct;

  return (
    <S.ListItem
      onClick={onClickRow}
      data-test="ProductListItem"
      selected={isSelected}
    >
      <S.CheckboxAndImageColumn>
        {isSelectingProducts && (
          <S.CompareCheckbox selected={isSelected} aria-label={product.name} />
        )}
        <Lazy offset={0}>
          <S.ImageContainer>
            <S.ListImage
              name={product.name}
              src={(product.media && product.media.first) || ''}
              fallback={product.category?.logo || undefined}
            />
          </S.ImageContainer>
        </Lazy>
      </S.CheckboxAndImageColumn>
      <div>
        <S.ProductName>
          {isSelectingProducts ? (
            highlightedName || product.name
          ) : (
            <S.ProductLink to={product.pathName} innerRef={ref}>
              {highlightedName || product.name}
            </S.ProductLink>
          )}
        </S.ProductName>
        <Rating
          rate={product.userReviewSummary.rating}
          singleStar
          showRate
          showCounter
          count={product.userReviewSummary.count}
          countTranslation={RATING_TRANSLATION}
          size="small"
        />
      </div>
      <S.PriceContainer>
        <Row gap={layout.spacing.rem.xSmall}>
          <ActiveCampaignBadge includedCampaigns={productCampaigns} />
          {product.priceSummary.regular && (
            <PriceLabel value={product.priceSummary.regular || {}} />
          )}
        </Row>
        <S.ShippingLabel
          toneDown
          exclShipping={product.priceSummary.regular || 0}
          inclShipping={product.priceSummary.includeShipping}
          currentSort={SortOptionEnum.PRICE}
        />
      </S.PriceContainer>
    </S.ListItem>
  );
};

type ListLayoutProps = {
  products: Product[];
  campaignsByProduct: CampaignIdsByProductId;
} & ProductComparisonProps;

export const ListLayout = ({
  products,
  campaignsByProduct,
  ...productComparisonProps
}: ListLayoutProps) => {
  return (
    <S.ListUl data-test="ProductLayoutList">
      <ContainerAdsList>
        {products.map(product => (
          <MobileListViewItem
            key={product.id}
            product={product}
            productCampaigns={campaignsByProduct[product.id]}
            highlightedName={
              product.highlight ? (
                <HighlightedSearchResult>
                  {product.highlight}
                </HighlightedSearchResult>
              ) : (
                undefined
              )
            }
            {...productComparisonProps}
          />
        ))}
      </ContainerAdsList>
    </S.ListUl>
  );
};
